<template>
  <v-alert
    v-if="!authStore.isLoggedIn"
    class="vAlert--info"
  >
    <template #title>
      {{ t('buyout.thank_you.common_user.account_activation') }}
    </template>
    <div class="desc">
      <ul>
        <li v-if="props.additionalInput">
          {{ t('buyout.thank_you.common_user.account_activation_subtext3') }}
        </li>
        <li>{{ t('buyout.thank_you.common_user.account_activation_subtext') }}</li>
        <li>{{ t('buyout.thank_you.common_user.account_activation_subtext2') }}</li>
      </ul>
      <div class="app__form">
        <template v-if="props.additionalInput">
          <div class="formItem formItem--1x6">
            <v-text-field
              v-model="activationForm.additionalActivationEmail"
              :label="t('buyout.detail.buyout_email')"
              :rules="[rules.isEmail]"
              :error-messages="get(errorMessages, ['email_phone', 'email'])"
              type="email"
              @input="resetErrorMessages(['email', 'email_phone'])"
            />
          </div>
          <div class="formItem formItem--1x6">
            <phone-field
              v-model="activationForm.additionalActivationPhoneNumber.suffix"
              name="customer_data_phone_number"
              :label="t('buyout.detail.buyout_telefon_number')"
              :error-messages="get(errorMessages, 'phone_number')"
              :default-country="selectedCountry.iso2"
              @input="resetErrorMessages(['phone_number', 'email_phone'])"
              @country-changed="handleCountryChange"
            />
          </div>
        </template>
        <div class="formItem formItem--1x6">
          <auth-password-field
            v-model="activationForm.password"
            :label="t('buyout.thank_you.common_user.password')"
            :validate-strength="true"
            :error-messages="get(errorMessages, 'password')"
            @change="resetErrorMessages(['password'])"
          />
        </div>
        <div class="formItem formItem--1x6">
          <auth-password-field
            v-model="activationForm.passwordAgain"
            :password-for-check="activationForm.password"
            :label="t('buyout.thank_you.common_user.password_again')"
            :validate-strength="false"
            :error-messages="get(errorMessages, 'password_again')"
            @change="resetErrorMessages(['password_again'])"
          />
        </div>
        <div class="formItem formItem--1x12 formItem--noLabel">
          <v-btn
            class="mbtn mbtn--blue"
            size="x-large"
            max-width="240"
            :loading="activateAccountLoading"
            @click="activateAccount"
          >
            {{ t('buyout.thank_you.common_user.activate') }}
          </v-btn>
        </div>
      </div>
    </div>
  </v-alert>
  <v-dialog
    v-model="otpDialogActive"
    content-class="popup aScroll"
  >
    <div
      class="popup__close cursor-pointer"
      @click="otpDialogActive = false;"
    >
      <v-icon icon="ct:cross" size="x-small" />
    </div>
    <v-card>
      <auth-otp-field
        :phone="formattedPhoneNumber"
        :email="email"
        :token="registerToken"
        :notify-text="t('form.register.register_successfully')"
      />
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { storeToRefs } from "pinia";
import { get } from "lodash-es";

const { locale, t } = useI18n();
const { notify } = useNotification();

const buyoutStore = useBuyoutStore();
const api = useApi();
const authStore = useAuthStore();
const { rules } = useFormValidators();
const commonStore = useCommonStore();
const { selectedCountry } = storeToRefs(commonStore);

const props = withDefaults(defineProps<{
  additionalInput?: boolean
  }>(), {
  additionalInput: false
});

const {
  userBuyoutCode,
  buyoutPersonalData
} = storeToRefs(buyoutStore);

const errorMessages = ref<Record<string, string>>({});

const email = ref<string>("");
const phoneNumber = ref<PhoneNumber>({
  prefix: `+${selectedCountry.value.dialCode}`,
  suffix: ""
});

const formattedPhoneNumber = computed<string | null>(() =>
  phoneNumber.value.suffix !== "" ? `${phoneNumber.value.prefix}${phoneNumber.value.suffix}` : null);

const registerToken = ref<string>("");

const emailVar = ref<string | null>(null);

const activateAccountLoading = ref<boolean>(false);

const activationForm = reactive<ActivationForm>({
  areaCode: "",
  additionalActivationEmail: "",
  additionalActivationPhoneNumber: {
    prefix: `+${selectedCountry.value.dialCode}`,
    suffix: ""
  },
  password: "",
  passwordAgain: ""
});

interface ActivationResponse {
    email: string;
    phone_number: string;
    register_token: string;
}

const otpDialogActive = ref<boolean>(false);

watch(
  [() => activationForm.additionalActivationEmail, () => activationForm.additionalActivationPhoneNumber.suffix],
  ([newEmail, newPhoneNumber]) => {
    if (newEmail !== undefined) {
      activationForm.additionalActivationEmail = newEmail;
    }
    if (newPhoneNumber !== undefined) {
      activationForm.additionalActivationPhoneNumber.suffix = newPhoneNumber;
    }

    if (activationForm.additionalActivationEmail === "" && activationForm.additionalActivationPhoneNumber.suffix === "") {
      errorMessages.value = { email_phone: t("form.validation.unfilled_phone_and_email") };
    }
  }
);

function resetErrorMessages (keys: string[]) {
  for (const key of keys) {
    if (errorMessages.value) {
      delete errorMessages.value[key];
    }
  }
}

async function activateAccount () {
  activateAccountLoading.value = true;

  if (props.additionalInput) {
    emailVar.value = activationForm.additionalActivationEmail.toLowerCase();
    phoneNumber.value = activationForm.additionalActivationPhoneNumber;
  } else {
    emailVar.value = buyoutPersonalData.value.email;
    phoneNumber.value = buyoutPersonalData.value.phone_number;
  }

  const { data, error } = await api.call<ActivationResponse>(api.commonUrls.auth.ACTIVATE_ACCOUNT(), {
    method: "POST",
    body: {
      phone_number: formattedPhoneNumber.value,
      email: emailVar.value,
      password: activationForm.password,
      password_again: activationForm.passwordAgain,
      type: "ambassador",
      registration_number: userBuyoutCode.value,
      language: locale.value
    }
  });

  if (data.value) {
    email.value = data.value.email;
    registerToken.value = data.value.register_token;
  }

  if (error.value) {
    if (error.value?.data) {
      if (error.value?.data?.already_exists) {
        notify({
          title: t("form.common.error"),
          text: t("buyout.detail.account_already_created"),
          type: "error"
        });
      } else if (error.value?.data.non_field_errors) {
        notify({
          title: t("form.common.error"),
          text: error.value?.data?.non_field_errors,
          type: "error"
        });
      } else {
        notify({
          title: t("form.common.error"),
          text: error.value?.data?.detail,
          type: "error"
        });
      }
    }
    errorMessages.value = error.value?.data;
  } else {
    notify({
      title: t("buyout.detail.activate_account_success"),
      type: "success"
    });
    otpDialogActive.value = true;
  }

  activateAccountLoading.value = false;
}

function handleCountryChange (selectedCountry: Country) {
  activationForm.additionalActivationPhoneNumber!.prefix = `+${selectedCountry.dialCode}`;
}

</script>

<style scoped>

</style>
